import React from "react";
import CampaignDetailsCard from "./CampaignDetailsCard";
import {
  formatNumber,
  formatPercentage,
  formatCurrency,
  formatDecimal,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";

interface DV360CardsProps {
  format: "DISPLAY" | "NATIVE" | "VIDEO" | "ALL" | undefined;
  mergedCampaignData:
    | components["schemas"]["MergedCampaignMetrics"]
    | null
    | undefined;
}

const DV360Cards = ({ format, mergedCampaignData }: DV360CardsProps) => {
  const { t } = useTranslation();
  const summary = mergedCampaignData?.metrics?.summary;

  /**
   * Reach y frecuencia, solo cuando la cifra es real.
   *
   * El backend marca `reachScope` a EXACT unicamente si pudo leer el reach del
   * periodo de una sola subcampana. En UNAVAILABLE no hay dato fiable —la
   * plataforma no lo devuelve, el report aun no ha corrido, o el grupo reune
   * varias subcampanas y el reach tampoco es sumable entre ellas— y entonces
   * no se pinta nada.
   *
   * Antes se pintaba siempre `uniqueUsers || 0`, que era la suma de los reach
   * diarios: el mismo usuario contado cada dia que veia el anuncio.
   */
  const reachCards = () => {
    if (summary?.reachScope !== "EXACT") return null;
    return (
      <>
        <CampaignDetailsCard
          value={formatNumber(summary.uniqueUsers)}
          label={t("campaigns.uniqueUsers", { defaultValue: "Unique Users" })}
        />
        <CampaignDetailsCard
          value={formatDecimal(summary.frequency)}
          label={t("campaigns.frequency", { defaultValue: "Frequency" })}
        />
      </>
    );
  };

  /**
   * Visibilidad, solo cuando hay dato real.
   *
   * Sin impresiones medibles la viewability no es 0%, es desconocida: Active
   * View no pudo medir. Pintar 0% seria afirmar que no se vio ninguna.
   *
   * Y hace falta exigir tambien que las visibles sean > 0. La columna
   * viewableImpressions se anadio con DEFAULT 0, asi que toda campana anterior
   * a esa release tiene medibles pero cero visibles: no porque nadie las viera,
   * sino porque la metrica no se pedia todavia. Con solo mirar las medibles,
   * una campana de agosto pintaba "Viewability 0.00%", que es exactamente la
   * afirmacion falsa que este guard existe para evitar.
   *
   * El coste de esto es que una campana con viewability genuinamente 0% no
   * mostraria la tarjeta. Es un escenario practicamente inexistente —implicaria
   * que ni una sola impresion medida entro en pantalla— y equivocarse hacia
   * "no se sabe" es mejor que hacia "no se vio".
   */
  const viewabilityCards = () => {
    if (!summary?.totalMeasurableImpressions) return null;
    if (!summary.totalViewableImpressions) return null;
    return (
      <>
        <CampaignDetailsCard
          value={formatPercentage(summary.avgViewability)}
          label={t("campaigns.viewability", { defaultValue: "Viewability" })}
        />
        <CampaignDetailsCard
          value={formatNumber(summary.totalViewableImpressions)}
          label={t("campaigns.viewableImpressions", {
            defaultValue: "Viewable Impressions",
          })}
        />
      </>
    );
  };

  return (
    <>
      {mergedCampaignData?.metrics?.summary && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            "& > *": {
              flex: "1 1 calc(20% - 24px)", // 5 cards per row (24px is the gap * (5-1)/5)
              minWidth: "180px",
            },
          }}
        >
          {format === "VIDEO" && (
            <>
              {/* VIDEO FORMAT METRICS */}
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.totalCost,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.investment", {
                  defaultValue: "Investment",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.visualizations || 0
                )}
                label={t("campaigns.visualizations", {
                  defaultValue: "Visualizations",
                })}
              />
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.avgCPV,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.cpv", { defaultValue: "CPV" })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.totalImpressions
                )}
                label={t("campaigns.impressions", {
                  defaultValue: "Impressions",
                })}
              />
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.avgCPM,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.cpm", { defaultValue: "CPM" })}
              />
              <CampaignDetailsCard
                value={formatPercentageNoMultiply(
                  mergedCampaignData.metrics.summary.avgVTR
                )}
                label={t("campaigns.vtr", { defaultValue: "VTR" })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.firstQuartile || 0
                )}
                label={t("campaigns.firstQuartile", {
                  defaultValue: "First Quartile",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.secondQuartile || 0
                )}
                label={t("campaigns.secondQuartile", {
                  defaultValue: "Second Quartile",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.thirdQuartile || 0
                )}
                label={t("campaigns.thirdQuartile", {
                  defaultValue: "Third Quartile",
                })}
              />
              {viewabilityCards()}
              {reachCards()}
            </>
          )}

          {format === "DISPLAY" && (
            <>
              {/* DISPLAY FORMAT METRICS */}
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.totalCost,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.investment", {
                  defaultValue: "Investment",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.totalImpressions
                )}
                label={t("campaigns.impressions", {
                  defaultValue: "Impressions",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.totalClicks
                )}
                label={t("campaigns.clicks", {
                  defaultValue: "Clicks",
                })}
              />
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.avgCPM,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.cpm", { defaultValue: "CPM" })}
              />
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.avgCPC,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.cpc", { defaultValue: "CPC" })}
              />
              <CampaignDetailsCard
                value={formatPercentage(
                  mergedCampaignData.metrics.summary.avgCTR
                )}
                label={t("campaigns.ctr", { defaultValue: "CTR" })}
              />
              <CampaignDetailsCard
                value={formatPercentageNoMultiply(
                  mergedCampaignData.metrics.summary.conversionRate || 0
                )}
                label={t("campaigns.conversionRate", {
                  defaultValue: "Conversion Rate",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.totalConversions || 0
                )}
                label={t("campaigns.totalConversions", {
                  defaultValue: "Conversions",
                })}
              />
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.cpa || 0,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.cpa", {
                  defaultValue: "CPA",
                })}
              />
              {viewabilityCards()}
              {reachCards()}
            </>
          )}

          {format === "NATIVE" && (
            <>
              {/* NATIVE FORMAT METRICS */}
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.totalCost,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.investment", {
                  defaultValue: "Investment",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.totalImpressions
                )}
                label={t("campaigns.impressions", {
                  defaultValue: "Impressions",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.totalClicks
                )}
                label={t("campaigns.clicks", {
                  defaultValue: "Clicks",
                })}
              />
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.avgCPC,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.cpc", { defaultValue: "CPC" })}
              />
              <CampaignDetailsCard
                value={formatPercentage(
                  mergedCampaignData.metrics.summary.avgCTR
                )}
                label={t("campaigns.ctr", { defaultValue: "CTR" })}
              />
              {reachCards()}
            </>
          )}

          {(!format || format === "ALL") && (
            <>
              {/* ALL FORMAT METRICS */}
              <CampaignDetailsCard
                value={formatCurrency(
                  mergedCampaignData.metrics.summary.totalCost,
                  mergedCampaignData.currency_enum
                )}
                label={t("campaigns.investment", {
                  defaultValue: "Investment",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.totalImpressions
                )}
                label={t("campaigns.impressions", {
                  defaultValue: "Impressions",
                })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.totalClicks
                )}
                label={t("campaigns.clicks", {
                  defaultValue: "Clicks",
                })}
              />
              <CampaignDetailsCard
                value={formatPercentage(
                  mergedCampaignData.metrics.summary.avgCTR
                )}
                label={t("campaigns.ctr", { defaultValue: "CTR" })}
              />
              <CampaignDetailsCard
                value={formatNumber(
                  mergedCampaignData.metrics.summary.totalConversions || 0
                )}
                label={t("campaigns.totalConversions", {
                  defaultValue: "Conversions",
                })}
              />
              <CampaignDetailsCard
                value={formatPercentageNoMultiply(
                  mergedCampaignData.metrics.summary.avgVTR || 0
                )}
                label={t("campaigns.vtr", { defaultValue: "VTR" })}
              />
              {viewabilityCards()}
              {reachCards()}
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default DV360Cards;
